import styles from './index.module.css'

import React from 'react'
import { Link } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import {
  clearSearch,
  fetchResults,
  selectSearchPage,
} from '@disco/state-management/reducers/search-page'

import type { FilterState } from 'types/filter-page'

import { DiscoCallout, Flex, Text } from 'components/ds'
import { APPEARS_ON_FILTER_DEFINITION } from 'components/search/search-filters/filter-definitions'
import { NO_PLAYLISTS_FILTER_VALUE } from 'components/search/search-filters/utils'

import { useDispatch, useSelector } from 'hooks/use-redux'

const NO_PLAYLISTS_FILTER: FilterState = {
  definition: APPEARS_ON_FILTER_DEFINITION,
  operator: 'equals',
  value: NO_PLAYLISTS_FILTER_VALUE,
}

type BannerProps = {
  eventProperties: Record<string, any>
}

const AppearsOnNoPlaylistsBanner = ({ eventProperties }: BannerProps) => {
  const dispatch = useDispatch()
  const initialFetchLoaded = useSelector(
    state => selectSearchPage(state).initialFetchLoaded,
  )

  // The banner only renders inside Browse's advanced search, so the search page
  // is always unmounted here and never fetches in response to the filter change
  const handleClick = () => {
    // clearSearch resets the query and commits the filter in one dispatch, so
    // the search page lands on a filter-only browse of zero-playlist tracks
    dispatch(clearSearch([NO_PLAYLISTS_FILTER]))
    mixpanel.sendPureEvent('Click Link', {
      Context: 'Search',
      Type: 'Banner: Appears on no playlists',
      Location: 'Explorer',
      ...eventProperties,
    })
    // The page auto-fetches on mount only when it has never loaded results, so
    // a return visit needs the fetch dispatched here
    if (initialFetchLoaded) dispatch(fetchResults())
  }

  return (
    <DiscoCallout
      type="feature"
      size="1"
      mt="2"
      dismissible
      dismissKey="appears_on_no_playlists_search_banner_dismissed"
    >
      <Flex mr="1">
        <Text wrap="pretty" className={styles.bannerText}>
          Did you know you can now find tracks that aren&apos;t in any
          playlists?{' '}
          <Link
            to="/search"
            onClick={handleClick}
            className={styles.bannerLink}
          >
            Show tracks with no playlists.
          </Link>
        </Text>
      </Flex>
    </DiscoCallout>
  )
}

export default AppearsOnNoPlaylistsBanner
